import { z } from 'zod';
import { SLIDESHOW_CAPS, SLIDESHOW_SPEEDS } from './use-slideshow';

/**
 * Préférence UTILISATEUR : options d'AFFICHAGE de la visionneuse, stockée CÔTÉ CLIENT
 * en `localStorage` — même patron que l'ordre par défaut de la galerie
 * (`gallery/default-sort-pref.ts`), le thème et la langue, sans endpoint serveur.
 *
 * Les bascules de la barre d'actions (nom de fichier, détail, note, commentaires) et
 * les réglages du diaporama (boucle, vitesse) étaient jusqu'ici des `useState` locaux
 * REMIS À ZÉRO à chaque ouverture. On les CONSERVE désormais entre sessions.
 *
 * Ne sont PAS persistés (contextuels au média courant) : la LECTURE du diaporama
 * (`playing`), l'échec d'image et la note affichée.
 *
 * Le module est PUR & testé, tolérant si `localStorage` est indisponible (mode privé
 * strict, quotas) : repli en mémoire → valeurs de fabrique.
 */

/** Clé de persistance (namespace `xphoto.*`, comme le thème et l'ordre par défaut). */
export const VIEWER_DISPLAY_STORAGE_KEY = 'xphoto.viewer.display';

/**
 * Index de vitesse de diaporama par défaut (5 s = 2ᵉ palier de `SLIDESHOW_SPEEDS`).
 * Aligné sur l'ancien `DEFAULT_SPEED_INDEX` de `use-slideshow`.
 */
const DEFAULT_SLIDESHOW_SPEED_INDEX = 1;

/**
 * Plafond par défaut appliqué à un média qu'on laisse aller jusqu'au bout (vidéo,
 * image animée) : 60 s = 3ᵉ palier de `SLIDESHOW_CAPS`. Assez long pour qu'un clip
 * passe en entier, assez court pour qu'un long métrage n'immobilise pas l'écran.
 */
const DEFAULT_SLIDESHOW_CAP_INDEX = 2;

/** Options d'affichage persistées de la visionneuse (booléens + un entier borné). */
export interface ViewerDisplayPref {
  showCaptions: boolean;
  showDetail: boolean;
  showRating: boolean;
  showComments: boolean;
  slideshowLoop: boolean;
  slideshowSpeedIndex: number;
  /**
   * Son des vidéos. Persisté parce qu'il est posé par le bouton MUET NATIF du
   * `<video>` : sans ça, le `<video>` étant remonté à chaque média (`key`), le son
   * activé retombait à muet à la diapositive suivante.
   */
  slideshowSound: boolean;
  /** Index dans `SLIDESHOW_CAPS` du plafond d'attente d'un média (0 = sans limite). */
  slideshowCapIndex: number;
}

/**
 * Valeurs de fabrique. Tout est replié SAUF la boucle du diaporama et la LÉGENDE COURTE
 * (`showCaptions`) : savoir QUELLE photo on regarde — nom de fichier et date — est une
 * information qu'on veut par défaut, pas une option à découvrir. L'utilisateur qui la
 * masque voit son choix retenu comme les autres bascules.
 */
export const VIEWER_DISPLAY_DEFAULTS: ViewerDisplayPref = {
  showCaptions: true,
  showDetail: false,
  showRating: false,
  showComments: false,
  slideshowLoop: true,
  slideshowSpeedIndex: DEFAULT_SLIDESHOW_SPEED_INDEX,
  // Muet par défaut : une bibliothèque qui se met à parler toute seule à l'ouverture
  // d'une vidéo serait une mauvaise surprise. C'est un choix que l'utilisateur POSE.
  slideshowSound: false,
  slideshowCapIndex: DEFAULT_SLIDESHOW_CAP_INDEX,
};

/**
 * Version du FORMAT persisté. Sert uniquement de marqueur de MIGRATION : une valeur
 * stockée sans `v` date d'avant que `showCaptions` passe à `true` par défaut.
 *
 * Le marqueur n'est pas un luxe ici. `Viewer` réécrit les quatre bascules dans un effet
 * qui tourne DÈS LE MONTAGE : quiconque a ouvert la visionneuse une seule fois a donc
 * déjà un objet stocké portant `showCaptions: false`. Changer la seule valeur de fabrique
 * n'aurait rien changé pour eux — c'est-à-dire pour à peu près tout le monde.
 */
const VIEWER_DISPLAY_VERSION = 1;

/**
 * Schéma de validation de l'objet persisté (JSON). `v` est OPTIONNEL : son absence
 * identifie précisément les valeurs d'avant la migration.
 */
const viewerDisplaySchema = z.object({
  showCaptions: z.boolean(),
  showDetail: z.boolean(),
  showRating: z.boolean(),
  showComments: z.boolean(),
  slideshowLoop: z.boolean(),
  slideshowSpeedIndex: z.number().int(),
  // OPTIONNELS, et ils doivent le rester : le schéma valide un objet DÉJÀ STOCKÉ chez
  // l'utilisateur. Exiger un champ ajouté après coup ferait échouer `safeParse` sur
  // toute préférence existante → repli silencieux sur les valeurs de fabrique, c.-à-d.
  // la PERTE des quatre bascules déjà réglées. Tout champ futur s'ajoute de même.
  slideshowSound: z.boolean().optional(),
  slideshowCapIndex: z.number().int().optional(),
  v: z.number().int().optional(),
});

/** Repli mémoire quand `localStorage` est inaccessible (jamais d'exception). */
let memoryPref: ViewerDisplayPref | null = null;

/**
 * Borne l'index de vitesse dans `[0, SLIDESHOW_SPEEDS.length - 1]` ; hors plage →
 * repli sur le défaut (`SLIDESHOW_SPEEDS` est lu ICI, à l'exécution, pour rester
 * insensible à l'ordre d'initialisation des modules — import cyclique).
 */
function clampSpeedIndex(index: number): number {
  return index >= 0 && index < SLIDESHOW_SPEEDS.length ? index : DEFAULT_SLIDESHOW_SPEED_INDEX;
}

/** Idem pour le plafond, borné dans `[0, SLIDESHOW_CAPS.length - 1]`. */
function clampCapIndex(index: number): number {
  return index >= 0 && index < SLIDESHOW_CAPS.length ? index : DEFAULT_SLIDESHOW_CAP_INDEX;
}

/** Parse JSON sans lever (retourne `null` sur entrée nulle ou malformée). */
function safeParseJson(raw: string | null): unknown {
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Valide + borne une valeur brute (JSON déjà parsé), ou `null` si invalide — SANS
 * migration. C'est le chemin d'ÉCRITURE : ce qu'on nous demande de persister est déjà
 * l'intention de l'utilisateur, la migration n'a rien à y faire (l'y appliquer
 * réécrirait un `showCaptions: false` tout juste choisi en `true`).
 */
function normalizePref(raw: unknown): ViewerDisplayPref | null {
  const parsed = viewerDisplaySchema.safeParse(raw);
  if (!parsed.success) return null;
  return {
    showCaptions: parsed.data.showCaptions,
    showDetail: parsed.data.showDetail,
    showRating: parsed.data.showRating,
    showComments: parsed.data.showComments,
    slideshowLoop: parsed.data.slideshowLoop,
    slideshowSpeedIndex: clampSpeedIndex(parsed.data.slideshowSpeedIndex),
    slideshowSound: parsed.data.slideshowSound ?? VIEWER_DISPLAY_DEFAULTS.slideshowSound,
    slideshowCapIndex: clampCapIndex(
      parsed.data.slideshowCapIndex ?? VIEWER_DISPLAY_DEFAULTS.slideshowCapIndex,
    ),
  };
}

/**
 * Chemin de LECTURE : validation + MIGRATION (une seule fois, par navigateur).
 *
 * Une valeur sans `v` précède le passage de `showCaptions` à `true` par défaut ; son
 * `false` n'exprime alors AUCUN choix — c'était l'ancienne valeur de fabrique, réécrite
 * au montage par l'effet de synchro de `Viewer`. On applique donc le nouveau défaut.
 * La migration reste idempotente jusqu'à la première écriture (qui pose `v`) : masquer
 * la légende écrit `{ showCaptions: false, v: 1 }`, et ce choix-là, exprimé, n'est plus
 * jamais retouché.
 */
function parseStoredPref(raw: unknown): ViewerDisplayPref | null {
  const parsed = viewerDisplaySchema.safeParse(raw);
  if (!parsed.success) return null;
  const normalized = normalizePref(parsed.data);
  if (normalized === null) return null;
  return parsed.data.v === undefined
    ? { ...normalized, showCaptions: VIEWER_DISPLAY_DEFAULTS.showCaptions }
    : normalized;
}

/**
 * Lit la préférence persistée, ou `null` si absente/invalide. Le repli MÉMOIRE
 * (`memoryPref`) n'est consulté QUE si `localStorage` est INDISPONIBLE (absent ou en
 * erreur) — pas quand il renvoie simplement `null` (aucune préférence) ni sur une
 * valeur malformée : ainsi vider `localStorage` remet bien aux valeurs de fabrique.
 */
export function readStoredViewerDisplay(): ViewerDisplayPref | null {
  try {
    if (typeof localStorage === 'undefined') return memoryPref;
    return parseStoredPref(safeParseJson(localStorage.getItem(VIEWER_DISPLAY_STORAGE_KEY)));
  } catch {
    return memoryPref;
  }
}

/** Préférence effective : persistée sinon les valeurs de fabrique. */
export function getViewerDisplayPref(): ViewerDisplayPref {
  return readStoredViewerDisplay() ?? VIEWER_DISPLAY_DEFAULTS;
}

/**
 * Persiste la préférence (repli mémoire + `localStorage`, silencieux si indisponible).
 * Une valeur mal formée est ignorée (garde-fou) ; l'index de vitesse est borné.
 */
export function storeViewerDisplay(pref: ViewerDisplayPref): void {
  const valid = normalizePref(pref);
  if (valid === null) return;
  memoryPref = valid;
  try {
    if (typeof localStorage !== 'undefined') {
      // `v` est écrit ICI et nulle part ailleurs : sa présence signe une valeur POSÉE par
      // l'utilisateur, donc à ne plus migrer (cf. `parseStoredPref`).
      localStorage.setItem(
        VIEWER_DISPLAY_STORAGE_KEY,
        JSON.stringify({ ...valid, v: VIEWER_DISPLAY_VERSION }),
      );
    }
  } catch {
    /* stockage indisponible : la préférence mémoire fait foi pour la session. */
  }
}

/**
 * Met à jour PARTIELLEMENT la préférence : lit l'état courant, applique `patch`, persiste.
 * Deux écrivains distincts partagent la clé (les bascules dans `Viewer`, la boucle/vitesse
 * dans `use-slideshow`) ; ce merge évite que l'un écrase les champs de l'autre.
 */
export function patchViewerDisplay(patch: Partial<ViewerDisplayPref>): void {
  storeViewerDisplay({ ...getViewerDisplayPref(), ...patch });
}
